import React from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { updateView } from 'components/spreadsheet/contexts/data/actions'
import { useApplicationStore } from 'hooks/application'
import useProject from 'hooks/useProject'
import { AddRows, DeleteRows, ImportData } from 'components/icons'
import { MenuItem } from 'components/menu/items'
import Toggle from 'components/toggle'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import { ITableViewWithColumns } from 'types'

/** Whether the open view has settings for this user: admins only, and not on a deleted table. */
export const hasViewSettings = (spreadsheetData: ISpreadsheetData): boolean =>
  spreadsheetData.isAdmin && !spreadsheetData.tableDetails.isDeleted

/**
 * A view's own settings: whether people can add, import or delete rows from it. In the view menu's View
 * settings section, opened from the view's tab, which needn't be the open view's. (Admins only, so never
 * inside a document, where no one is an admin of the table.) Each row flips its switch, as the switch does;
 * a setting that can't change says why under its name. A Custom View has add and delete but not import.
 */
const ViewSettingsItems: React.FC<{ view: ITableViewWithColumns }> = ({ view }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()

  const isSpreadsheet = view.type === ViewTypes.SPREADSHEET
  if (!hasViewSettings(spreadsheetData) || (!isSpreadsheet && view.type !== ViewTypes.CUSTOM_VIEW)) return null

  const { tableDetails } = spreadsheetData
  const synced = tableDetails.isSynced ? 'Off while the table syncs' : undefined

  const setViewField = (field: 'disableNewRow' | 'disableSyncCsv' | 'allowContributorDelete', value: boolean) =>
    updateView(
      field,
      spreadsheetData,
      setSpreadsheetData,
      view.publicId,
      value,
      project.publicId,
      () => setSnackbarMessage({ status: 'success', message: 'Save has been successful!' }),
      (error: any) => displayErrorMessage(error)
    )

  const setting = (
    name: string,
    icon: React.ReactNode,
    on: boolean,
    flip: (on: boolean) => void,
    why?: string
  ): React.ReactNode => (
    <MenuItem
      icon={icon}
      description={why}
      disabled={!!why}
      onClick={() => flip(!on)}
      end={<Toggle aria-label={name} toggled={on} onToggle={flip} disabled={!!why} />}
    >
      {name}
    </MenuItem>
  )

  const addAllowed = !view.disableNewRow && !tableDetails.isSynced

  return (
    <>
      {setting('Allow Add New Row', <AddRows />, addAllowed, (on) => setViewField('disableNewRow', !on), synced)}
      {isSpreadsheet &&
        setting(
          'Allow Data Import',
          <ImportData />,
          !view.disableSyncCsv && addAllowed,
          (on) => setViewField('disableSyncCsv', !on),
          synced ?? (view.disableNewRow ? 'Needs Allow Add New Row' : undefined)
        )}
      {setting(
        'Allow Row Deletion',
        <DeleteRows />,
        view.allowContributorDelete && !tableDetails.isSynced,
        (on) => setViewField('allowContributorDelete', on),
        synced
      )}
    </>
  )
}

export default ViewSettingsItems
